import { useEffect, useMemo, useRef, useState } from 'react'
import { brushX, type D3BrushEvent } from 'd3-brush'
import { select } from 'd3-selection'
import type { TrackPoint } from '@xingroute/schema'
import { bucketColor, smoothGrades } from '../lib/gpx.js'
import { useElevationCursor } from '../hooks/useElevationCursor.js'
import { useLocale } from '../locale/LocaleContext.js'

export interface ElevationProfileProps {
  track: TrackPoint[]
  totalDistanceKm?: number
  onHover?: (idx: number, grade: number | null) => void
}

interface TimedTrackPoint extends TrackPoint {
  time?: string
  timestamp?: string
}

interface ProfilePoint {
  idx: number
  distanceM: number
  ele: number
  grade: number | null
  timeMs?: number
}

interface AreaSlice {
  /** SVG polygon path covering the area between the elevation curve and
   *  the chart baseline for one consecutive pair of profile points. */
  d: string
  color: string
}

type AxisMode = 'distance' | 'time'

const WIDTH = 640
const HEIGHT = 132
const PAD_X = 12
const PAD_TOP = 12
const CHART_BOTTOM = 110
const R = 6_371_000

function toRad(d: number): number {
  return (d * Math.PI) / 180
}

function haversine(a: TrackPoint, b: TrackPoint): number {
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
}

function fmtKm(meters: number): string {
  const km = meters / 1000
  return km >= 10 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`
}

function fmtAxisKm(meters: number, precision: 1 | 2 = 1): string {
  const km = meters / 1000
  if (precision === 2) return `${km.toFixed(2)} km`
  return `${km.toFixed(1)} km`
}

function fmtDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, '0')}m`
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function pointTimeMs(point: TrackPoint): number | undefined {
  const timed = point as TimedTrackPoint
  const raw = timed.timestamp ?? timed.time
  if (!raw) return undefined
  const ms = Date.parse(raw)
  return Number.isFinite(ms) ? ms : undefined
}

function axisValue(point: ProfilePoint, mode: AxisMode, timeStartMs: number): number {
  return mode === 'time' && point.timeMs !== undefined
    ? point.timeMs - timeStartMs
    : point.distanceM
}

function xForAxis(value: number, max: number): number {
  const span = WIDTH - PAD_X * 2
  return PAD_X + (max > 0 ? (value / max) * span : 0)
}

function yForEle(ele: number, minEle: number, maxEle: number): number {
  const span = CHART_BOTTOM - PAD_TOP
  const eleSpan = Math.max(1, maxEle - minEle)
  return PAD_TOP + ((maxEle - ele) / eleSpan) * span
}

function buildPath(
  points: ProfilePoint[],
  minEle: number,
  maxEle: number,
  axisMode: AxisMode,
  timeStartMs: number,
  axisMax: number,
): string {
  return points
    .map((p, i) => {
      const x = xForAxis(axisValue(p, axisMode, timeStartMs), axisMax)
      const y = yForEle(p.ele, minEle, maxEle)
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')
}

function nearestByDistance(points: ProfilePoint[], distanceM: number): ProfilePoint {
  let lo = 0
  let hi = points.length - 1
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (points[mid]!.distanceM < distanceM) lo = mid + 1
    else hi = mid
  }
  const next = points[lo]!
  const prev = points[Math.max(0, lo - 1)]!
  return Math.abs(prev.distanceM - distanceM) <= Math.abs(next.distanceM - distanceM) ? prev : next
}

function distanceAtAxis(
  points: ProfilePoint[],
  axis: number,
  mode: AxisMode,
  timeStartMs: number,
): number {
  if (mode === 'distance') return axis
  let lo = 0
  let hi = points.length - 1
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (axisValue(points[mid]!, mode, timeStartMs) < axis) lo = mid + 1
    else hi = mid
  }
  const right = points[lo]!
  const left = points[Math.max(0, lo - 1)]!
  const leftAxis = axisValue(left, mode, timeStartMs)
  const rightAxis = axisValue(right, mode, timeStartMs)
  if (rightAxis <= leftAxis) return right.distanceM
  const t = Math.min(1, Math.max(0, (axis - leftAxis) / (rightAxis - leftAxis)))
  return left.distanceM + (right.distanceM - left.distanceM) * t
}

function buildAreaSlices(
  points: ProfilePoint[],
  minEle: number,
  maxEle: number,
  axisMode: AxisMode,
  timeStartMs: number,
  axisMax: number,
): AreaSlice[] {
  const slices: AreaSlice[] = []
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!
    const b = points[i]!
    const x1 = xForAxis(axisValue(a, axisMode, timeStartMs), axisMax)
    const x2 = xForAxis(axisValue(b, axisMode, timeStartMs), axisMax)
    if (x2 <= x1) continue
    const y1 = yForEle(a.ele, minEle, maxEle)
    const y2 = yForEle(b.ele, minEle, maxEle)
    const d = `M ${x1.toFixed(2)} ${CHART_BOTTOM} L ${x1.toFixed(2)} ${y1.toFixed(2)} L ${x2.toFixed(2)} ${y2.toFixed(2)} L ${x2.toFixed(2)} ${CHART_BOTTOM} Z`
    // Segment's grade lives on `b` (segment ending at b); both raw and
    // smoothed grades have already been written there in `profile`.
    slices.push({ d, color: bucketColor(b.grade) })
  }
  return slices
}

function cursorPoint(
  points: ProfilePoint[],
  cursorKm: number | null,
  minEle: number,
  maxEle: number,
  axisMode: AxisMode,
  timeStartMs: number,
  axisMax: number,
): { x: number; y: number } | null {
  if (cursorKm === null) return null
  const targetM = cursorKm * 1000
  if (targetM < 0 || targetM > points[points.length - 1]!.distanceM) return null
  let lo = 0
  let hi = points.length - 1
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (points[mid]!.distanceM < targetM) lo = mid + 1
    else hi = mid
  }
  const right = points[lo]!
  const left = points[Math.max(0, lo - 1)]!
  const seg = Math.max(1, right.distanceM - left.distanceM)
  const t = Math.min(1, Math.max(0, (targetM - left.distanceM) / seg))
  const ele = left.ele + (right.ele - left.ele) * t
  const axis =
    axisMode === 'time' && left.timeMs !== undefined && right.timeMs !== undefined
      ? left.timeMs - timeStartMs + (right.timeMs - left.timeMs) * t
      : targetM
  return { x: xForAxis(axis, axisMax), y: yForEle(ele, minEle, maxEle) }
}

export function ElevationProfile({ track, totalDistanceKm, onHover }: ElevationProfileProps) {
  const brushRef = useRef<SVGGElement | null>(null)
  const hoverFrameRef = useRef<number | null>(null)
  const pendingCursorKmRef = useRef<number | null>(null)
  const [axisMode, setAxisMode] = useState<AxisMode>('distance')
  const { locale, t } = useLocale()
  const { cursorKm, setCursorKm, setSelectedRangeKm } = useElevationCursor()

  const profile = useMemo(() => {
    const points: ProfilePoint[] = []
    const distances: number[] = []
    let distanceM = 0
    let minEle = Infinity
    let maxEle = -Infinity
    for (let i = 0; i < track.length; i++) {
      const point = track[i]!
      if (i > 0) distanceM += haversine(track[i - 1]!, point)
      distances.push(distanceM)
      if (point.ele === undefined) continue
      minEle = Math.min(minEle, point.ele)
      maxEle = Math.max(maxEle, point.ele)
      // Per-segment grade is filled below from `smoothGrades`; we leave it
      // null here so the first point (no incoming segment) stays null.
      points.push({ idx: i, distanceM, ele: point.ele, grade: null, timeMs: pointTimeMs(point) })
    }
    // smoothGrades returns one value per segment of the *raw* track. We
    // map those back onto the `points` array (which may have skipped
    // ele-less raw points): each profile point inherits the smoothed grade
    // of the raw segment that ends at it.
    const smoothed = smoothGrades(
      track.map((p) => ({ lat: p.lat, lon: p.lon, ele: p.ele })),
      50,
    )
    for (const point of points) {
      const segIdx = point.idx - 1
      if (segIdx >= 0 && segIdx < smoothed.length) {
        point.grade = smoothed[segIdx] ?? null
      }
    }

    let hasTime = points.length >= 2
    let prevTime = -Infinity
    for (const point of points) {
      if (point.timeMs === undefined || point.timeMs < prevTime) {
        hasTime = false
        break
      }
      prevTime = point.timeMs
    }
    const timeStartMs = hasTime ? points[0]!.timeMs! : 0
    const timeEndMs = hasTime ? points[points.length - 1]!.timeMs! : 0
    if (timeEndMs <= timeStartMs) hasTime = false

    return { points, minEle, maxEle, totalM: distanceM, hasTime, timeStartMs, timeEndMs }
  }, [track])

  useEffect(() => {
    setCursorKm(null)
    setSelectedRangeKm(null)
  }, [setCursorKm, setSelectedRangeKm, track])

  useEffect(() => {
    if (!profile.hasTime && axisMode === 'time') setAxisMode('distance')
  }, [axisMode, profile.hasTime])

  useEffect(() => {
    return () => {
      if (hoverFrameRef.current !== null) cancelAnimationFrame(hoverFrameRef.current)
    }
  }, [])

  const fullDistanceM =
    totalDistanceKm !== undefined && Number.isFinite(totalDistanceKm) && totalDistanceKm > 0
      ? totalDistanceKm * 1000
      : profile.totalM

  const axisMax =
    axisMode === 'time' && profile.hasTime ? profile.timeEndMs - profile.timeStartMs : fullDistanceM

  const chart = useMemo(() => {
    if (profile.points.length < 2) return null
    const linePath = buildPath(
      profile.points,
      profile.minEle,
      profile.maxEle,
      axisMode,
      profile.timeStartMs,
      axisMax,
    )
    const slices = buildAreaSlices(
      profile.points,
      profile.minEle,
      profile.maxEle,
      axisMode,
      profile.timeStartMs,
      axisMax,
    )
    const first = profile.points[0]!
    const midLabel =
      axisMode === 'time' && profile.hasTime ? fmtDuration(axisMax / 2) : fmtAxisKm(axisMax / 2)
    return {
      linePath,
      slices,
      firstLabel: axisMode === 'time' && profile.hasTime ? '0:00' : fmtKm(first.distanceM),
      midLabel,
      lastLabel:
        axisMode === 'time' && profile.hasTime ? fmtDuration(axisMax) : fmtAxisKm(axisMax, 2),
    }
  }, [axisMax, axisMode, profile])

  const hoverPoint = useMemo(() => {
    if (profile.points.length < 2) return null
    return cursorPoint(
      profile.points,
      cursorKm,
      profile.minEle,
      profile.maxEle,
      axisMode,
      profile.timeStartMs,
      axisMax,
    )
  }, [axisMax, axisMode, cursorKm, profile])

  useEffect(() => {
    const node = brushRef.current
    if (!node || profile.points.length < 2) return

    const brush = brushX<unknown>()
      .extent([
        [PAD_X, PAD_TOP],
        [WIDTH - PAD_X, CHART_BOTTOM],
      ])
      .on('end', (event: D3BrushEvent<unknown>) => {
        const selection = event.selection
        if (!selection) {
          setSelectedRangeKm(null)
          return
        }
        const [rawX0, rawX1] = selection as [number, number]
        const x0 = Math.max(PAD_X, Math.min(WIDTH - PAD_X, rawX0))
        const x1 = Math.max(PAD_X, Math.min(WIDTH - PAD_X, rawX1))
        const span = WIDTH - PAD_X * 2
        const axis0 = ((Math.min(x0, x1) - PAD_X) / span) * axisMax
        const axis1 = ((Math.max(x0, x1) - PAD_X) / span) * axisMax
        const fromM = distanceAtAxis(profile.points, axis0, axisMode, profile.timeStartMs)
        const toM = distanceAtAxis(profile.points, axis1, axisMode, profile.timeStartMs)
        setSelectedRangeKm([fromM / 1000, toM / 1000])
      })

    const selection = select(node)
    selection.call(brush)
    selection.selectAll<SVGRectElement, unknown>('.overlay').attr('cursor', 'crosshair')
    return () => {
      selection.on('.brush', null)
      selection.selectAll('*').remove()
    }
  }, [axisMax, axisMode, profile.points, profile.timeStartMs, setSelectedRangeKm])

  if (profile.points.length < 2 || !chart) {
    return (
      <div className="h-20 w-full">
        <div className="flex h-full items-center">
          <div className="h-px w-full bg-[var(--xr-text-muted)] opacity-35" />
        </div>
      </div>
    )
  }

  // NOTE: the previous Distance / Ascent / Descent stat tiles row that used
  // to appear above the chart whenever a range was brushed has been removed
  // — selection stats now live in the below-chart caption rendered by
  // `RouteElevationSection` (see wiki/entities/web-route-detail-page.md
  // §Elevation segment selection). Keeping the chart height constant means
  // the curve no longer reflows mid-interaction, which used to read as if
  // the elevation values themselves had changed.

  return (
    <div className="relative flex h-36 w-full flex-col gap-2" data-testid="elevation-profile">
      {profile.hasTime && (
        <div className="flex flex-none items-center justify-end gap-2">
          <div className="inline-flex rounded-[var(--xr-radius-full)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface-elev)] p-0.5 text-[10px]">
            <button
              type="button"
              className={`rounded-[var(--xr-radius-full)] px-2 py-0.5 ${
                axisMode === 'distance'
                  ? 'bg-[var(--xr-brand-primary)] text-[var(--xr-brand-primary-ink)]'
                  : 'text-[var(--xr-text-muted)]'
              }`}
              onClick={() => setAxisMode('distance')}
            >
              {locale === 'zh' ? '距离' : 'Distance'}
            </button>
            <button
              type="button"
              className={`rounded-[var(--xr-radius-full)] px-2 py-0.5 ${
                axisMode === 'time'
                  ? 'bg-[var(--xr-brand-primary)] text-[var(--xr-brand-primary-ink)]'
                  : 'text-[var(--xr-text-muted)]'
              }`}
              onClick={() => setAxisMode('time')}
            >
              {locale === 'zh' ? '时间' : 'Time'}
            </button>
          </div>
        </div>
      )}
      <div className="relative min-h-0 flex-1">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="none"
          className="h-full w-full overflow-visible"
          role="img"
          aria-label={t('aria.elevationProfile')}
          onMouseMove={(event) => {
            const rect = event.currentTarget.getBoundingClientRect()
            const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
            const axis = ratio * axisMax
            const distanceM = distanceAtAxis(profile.points, axis, axisMode, profile.timeStartMs)
            const nearest = nearestByDistance(profile.points, distanceM)
            onHover?.(nearest.idx, nearest.grade)
            pendingCursorKmRef.current = distanceM / 1000
            if (hoverFrameRef.current === null) {
              hoverFrameRef.current = requestAnimationFrame(() => {
                hoverFrameRef.current = null
                setCursorKm(pendingCursorKmRef.current)
              })
            }
          }}
          onMouseLeave={() => {
            onHover?.(-1, null)
            pendingCursorKmRef.current = null
            if (hoverFrameRef.current !== null) {
              cancelAnimationFrame(hoverFrameRef.current)
              hoverFrameRef.current = null
            }
            setCursorKm(null)
          }}
        >
          <g aria-label={t('aria.gradeBand')}>
            {chart.slices.map((slice, i) => (
              <path key={`${i}-${slice.color}`} d={slice.d} fill={slice.color} opacity="0.85" />
            ))}
          </g>
          <path
            d={chart.linePath}
            fill="none"
            stroke="var(--xr-text-primary)"
            strokeOpacity="0.55"
            strokeWidth="1.25"
          />
          {hoverPoint && (
            <g pointerEvents="none" aria-hidden>
              {/* Cursor line + dot are now rendered in the HTML overlay
               * below the SVG so the dot stays a perfect circle even
               * when preserveAspectRatio="none" stretches the SVG. We
               * keep an empty <g> as an aria-hidden anchor purely for
               * test-id continuity (avoids churn in DOM snapshots). */}
            </g>
          )}
          <g ref={brushRef} className="xr-elevation-brush" />
        </svg>
        {hoverPoint && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            data-testid="elevation-cursor-overlay"
          >
            {/* Vertical cursor line — width is fixed pixel so it stays
             * crisp regardless of container width. The SVG above fills
             * its container (h-full w-full) with preserveAspectRatio=none,
             * so a viewBox x of `hoverPoint.x` maps linearly to the
             * container width fraction `hoverPoint.x / WIDTH`. */}
            <div
              className="absolute w-px bg-[var(--xr-text-primary)] opacity-55"
              style={{
                left: `${(hoverPoint.x / WIDTH) * 100}%`,
                top: `${(PAD_TOP / HEIGHT) * 100}%`,
                height: `${((CHART_BOTTOM - PAD_TOP) / HEIGHT) * 100}%`,
              }}
            />
            {/* Cursor dot — fixed pixel size + border-radius:50% ensures
             * it is always a perfect circle even when the SVG itself is
             * stretched non-uniformly. */}
            <div
              className="absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--xr-bg-surface)]"
              style={{
                left: `${(hoverPoint.x / WIDTH) * 100}%`,
                top: `${(hoverPoint.y / HEIGHT) * 100}%`,
                boxShadow: '0 0 0 2px var(--xr-brand-primary)',
              }}
            />
          </div>
        )}
        {/* The "Lowest X · Highest Y" caption that used to sit at the top
         * right of the chart is removed — the RouteElevationSection caption
         * below the chart already shows Min / Max / Max grade once. */}
        <div className="pointer-events-none absolute inset-x-1 bottom-0 flex justify-between text-[10px] leading-none text-[var(--xr-text-muted)]">
          <span>{chart.firstLabel}</span>
          <span>{chart.midLabel}</span>
          <span>{chart.lastLabel}</span>
        </div>
      </div>
    </div>
  )
}
